import React, { useState, useEffect, useCallback } from "react";
import "../../style/ChallanForm.css";
import { toast } from "react-toastify";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { FaFileAlt } from "react-icons/fa";
import api from "../../api/axiosInstance";
import InsertProduct from "../../components/InsertProduct";
import { TokenManager } from "../../utils/tokenManager";
import { BASE_URL, API_ENDPOINTS } from "../../config/apiconfig";
import { challanApi } from "../../api/challanApi";
import { getErrorMessage } from "../../utils/errorHandler";

function ChallanForm() {

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [showInsertProduct, setShowInsertProduct] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [challanNumberChecking, setChallanNumberChecking] = useState(false);
  const [challanNumberError, setChallanNumberError] = useState("");
  const [challanNumberValid, setChallanNumberValid] = useState(null);
  const [originalChallanNumber, setOriginalChallanNumber] = useState("");

  const [form, setForm] = useState({
    customerId: "",
    challanNumber: "",
    // batchNo: "",
    customer: "",
    challanType: "",
    date: new Date().toISOString().split("T")[0],
    siteLocation: "",
    vehicleNo: "",
    transporter: "",
    driverDetails: "",
    deliveryDetails: "",
    poNo: "",
    depositType: "Deposit",
    depositAmount: "",
    depositNarration: "",
    products: [],
  });
  const displayDepositAmount =
    form.depositType === "Returned" && form.depositAmount !== ""
      ? `-${String(form.depositAmount).replace(/^-/, "")}`
      : form.depositAmount;


  // Add Products
  const handleProductsAdded = (products) => {

    const mappedProducts = products.map((p) => ({
      name: p.product,
      qty:
        // form.challanType?.includes("Delivered") || !form.challanType
        form.challanType === "DELIVERED" || !form.challanType
          ? Number(p.delivered)
          : Number(p.received),
    }));

    setForm((prev) => ({
      ...prev,
      products: mappedProducts,
    }));

    setShowInsertProduct(false);
    toast.success("Products added successfully!");
  };

  // Function to fetch existing challan data
  const fetchChallanData = useCallback(async () => {
    try {
      const token = TokenManager.getToken();
      if (!token) {
        toast.error("Session expired. Please login again.");
        navigate("/");
        return;
      }

      const response = await api.get(`${API_ENDPOINTS.CHALLANS.GET_BY_ID(id)}`);
      if (response.status === 200) {
        const challanData = response.data;
        console.log("Fetched challan data:", challanData);
        
        const originalChallanNum = challanData.challanNumber || "";
        setOriginalChallanNumber(originalChallanNum);
        
        // Map the API response to form fields
        setForm({
          customerId: challanData.customerId || "",
          challanNumber: originalChallanNum,
          customer: challanData.customerName || "",
          challanType: challanData.challanType || "",
          date: challanData.date ? challanData.date.split("T")[0] : new Date().toISOString().split("T")[0],
          siteLocation: challanData.siteLocation || "",
          vehicleNo: challanData.vehicleNumber || "",
          transporter: challanData.transporter || "",
          driverDetails: challanData.driverName || "",
          deliveryDetails: challanData.deliveryDetails || "",
          poNo: challanData.purchaseOrderNo || "",
          depositType: challanData.depositeNarration ? "Returned" : "Deposit",
          depositAmount: challanData.depositeAmount || "",
          depositNarration: challanData.depositeNarration || "",
          products: challanData.items ? challanData.items.map(item => ({
            name: item.name,
            qty: item.deliveredQty || item.receivedQty || 0
          })) : []
        });
        
        // In edit mode, the existing challan number is valid
        setChallanNumberValid(true);
        setChallanNumberError("");
        
        toast.success("Challan data loaded successfully!");
      } else {
        toast.error("Failed to fetch challan data");
      }
    } catch (error) {
      console.error("Error fetching challan data:", error);
      toast.error("Error loading challan data");
    }
  }, [id, navigate]);

  // Fetch existing challan data when in edit mode
  useEffect(() => {
    if (id) {
      // We're in edit mode
      setIsEdit(true);
      fetchChallanData();
    } else {
      // We're in create mode
      setIsEdit(false);
    }

    if (location.state?.products) {
      const mappedProducts = location.state.products.map((p) => ({
        name: p.product,
        qty:
          form.challanType === "DELIVERED" || !form.challanType
            ? Number(p.delivered)
            : Number(p.received),
      }));

      setForm((prev) => ({
        ...prev,
        products: mappedProducts,
      }));
    }
  }, [id, location.state, fetchChallanData, form.challanType]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearTimeout(window.challanNumberCheckTimeout);
    };
  }, []);

//   setForm({
//     ...form,
//     [name]: value,
//   });m
// };
  const fetchNextAvailableChallanNumber = useCallback(async (preferredNumber) => {
    try {
      const response = await challanApi.getNextAvailableManualChallanNumber(preferredNumber);
      return response.data;
    } catch (error) {
      console.error("Error fetching next available challan number:", error);
      return null;
    }
  }, []);

  const checkChallanNumberAvailability = useCallback(async (challanNumber) => {
    if (!challanNumber || challanNumber.trim() === "") {
      setChallanNumberValid(null);
      setChallanNumberError("");
      return;
    }

    const trimmedNumber = challanNumber.trim();

    // In edit mode, allow the existing challan number to remain unchanged
    if (isEdit && originalChallanNumber && originalChallanNumber === trimmedNumber) {
      setChallanNumberValid(true);
      setChallanNumberError("");
      setChallanNumberChecking(false);
      return;
    }

    setChallanNumberChecking(true);
    setChallanNumberError("");
    setChallanNumberValid(null);

    try {
      const response = await challanApi.checkChallanNumberAvailability(trimmedNumber);
      const isAvailable = response.data;
      if (isAvailable) {
        setChallanNumberValid(true);
        return;
      }

      // Duplicate found — auto-increment to the next available number
      const nextAvailable = await fetchNextAvailableChallanNumber(trimmedNumber);
      if (nextAvailable && nextAvailable !== trimmedNumber) {
        setForm((prev) => ({ ...prev, challanNumber: nextAvailable }));
        setChallanNumberValid(true);
        setChallanNumberError("");
        toast.info(`Challan number "${trimmedNumber}" is already in use. Auto-incremented to ${nextAvailable}.`);
      } else {
        setChallanNumberValid(false);
        setChallanNumberError("Challan number already exists. Please use a unique number.");
      }
    } catch (error) {
      console.error("Error checking challan number:", error);
      setChallanNumberValid(null);
      setChallanNumberError("Failed to verify challan number");
    } finally {
      setChallanNumberChecking(false);
    }
  }, [isEdit, originalChallanNumber, fetchNextAvailableChallanNumber]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm({ 
      ...form,
      [name]: value, 
    });

    // Check challan number availability when challanNumber field changes
    if (name === "challanNumber") {
      // Debounce the check
      clearTimeout(window.challanNumberCheckTimeout);
      window.challanNumberCheckTimeout = setTimeout(() => {
        checkChallanNumberAvailability(value);
      }, 300);
    }
  };
  const handleReset = () => {

    setForm({
      customerId: "",
      challanNumber: "",
      // batchNo: "",
      customer: "",
      challanType: "",
      date: new Date().toISOString().split("T")[0],
      siteLocation: "",
      vehicleNo: "",
      transporter: "",
      driverDetails: "",
      deliveryDetails: "",
      poNo: "",
      depositType: "Deposit",
      depositAmount: "",
      depositNarration: "",
      products: [],
    });
    
    setOriginalChallanNumber("");
    setChallanNumberValid(null);
    setChallanNumberError("");
    setChallanNumberChecking(false);
    clearTimeout(window.challanNumberCheckTimeout);

    toast.info("Form reset");
  };

  const handleSubmit = async (e) => {

    e.preventDefault();

    console.log("Submit clicked");
    console.log("Form Data:", form);

    if (
      !form.challanNumber ||
      // !form.batchNo ||
      !form.customer ||
      !form.siteLocation ||
      !form.vehicleNo ||
      !form.transporter ||
      !form.challanType
    ) {
      toast.error("Please fill all required fields.");
      return;
    }

    if (form.products.length === 0) {
      toast.error("Please insert products.");
      return;
    }

    // Validate challan number uniqueness
    // In edit mode, allow the existing challan number to remain unchanged
    const isChallanNumberChanged = isEdit && originalChallanNumber && form.challanNumber !== originalChallanNumber;
    if (challanNumberValid === false && !isChallanNumberChanged) {
      toast.error(challanNumberError || "Challan number already exists. Please use a unique number.");
      return;
    }
    
    // If validation hasn't been done yet, do a quick check (skip in edit mode if unchanged)
    if (challanNumberValid === null && form.challanNumber && !(isEdit && originalChallanNumber && form.challanNumber === originalChallanNumber)) {
      try {
        const response = await challanApi.checkChallanNumberAvailability(form.challanNumber.trim());
        if (!response.data) {
          toast.error("Challan number already exists. Please use a unique number.");
          return;
        }
      } catch (error) {
        console.warn("Could not verify challan number, proceeding anyway:", error);
      }
    }

    try {

      setLoading(true);

      // const token = localStorage.getItem("token");
      // console.log("Token:", token);
        // ✅ FIX: use TokenManager instead of localStorage
const token = TokenManager.getToken();

// ✅ safety check (important)
if (!token) {
  toast.error("Session expired. Please login again.");
  navigate("/");
  return;
}
      
const payload = {
  customerId: form.customerId ? Number(form.customerId) : null,
  customerName: form.customer,
  challanNumber: form.challanNumber,
  // batchNo: form.batchNo,
  challanType: form.challanType,
  date: form.date,
  siteLocation: form.siteLocation,
  vehicleNumber: form.vehicleNo,
  transporter: form.transporter,
  driverName: form.driverDetails,
  driverNumber: "", 
  deliveryDetails: form.deliveryDetails,
  purchaseOrderNo: form.poNo,
  depositType: form.depositType,
  depositeType: form.depositType,
  depositAmount: Number(form.depositAmount) || 0,
  depositeAmount: Number(form.depositAmount) || 0,
  depositInformation: form.depositNarration || "",
  depositeInformation: form.depositNarration || "",
  depositeNarration: form.depositNarration,
  returnedAmount: form.depositType === "Returned" ? -Math.abs(Number(form.depositAmount) || 0) : Number(form.depositAmount) || 0,
  items: form.products.map((p) => ({
    name: p.name,
    deliveredQty: form.challanType === "DELIVERED" ? Number(p.qty) : 0,
    receivedQty: form.challanType === "RECEIVED" ? Number(p.qty) : 0,
  })),
};

      console.log("Payload:", payload);
      console.log("Request URL:", `${BASE_URL}/challans/create`);

      // Use different API calls for create vs update
      let response;
      if (isEdit) {
        // Use combined challan update endpoint for COMBINED type
        console.log("Challan type for update:", form.challanType);
        if (form.challanType === "COMBINED") {
          console.log("Using combined challan update endpoint");
          response = await challanApi.updateCombinedChallan(id, payload);
        } else {
          console.log("Using regular challan update endpoint");
          response = await api.put(`${API_ENDPOINTS.CHALLANS.UPDATE(id)}`, payload);
        }
        console.log("Updating challan with ID:", id);
      } else {
        response = await api.post("challans/create", payload);
        console.log("Creating new challan");
      }

      console.log("Response status:", response.status);
      console.log("Response data:", response.data);

      if (response.status === 201 || response.status === 200) {
        const successMessage = isEdit ? "Challan Updated Successfully!" : "Challan Saved Successfully!";
        toast.success(successMessage);
        setTimeout(() => {
          navigate("/dashboard/challans");
        }, 1500);
      } else {
        toast.error("Unexpected response status: " + response.status);
      }

    } catch (error) {
      console.error(error);
      
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      
      // Handle authentication errors separately
      if (error.response?.status === 401) {
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="cf-container">

        <div className="cf-card"> 

          <div className="cf-header">

            <div className="cf-header-left">
              <FaFileAlt className="cf-icon" />
              <h2>{isEdit ? 'Edit' : 'Delivered / Received'} Challan Entry</h2>
            </div>

          </div>

          <form onSubmit={handleSubmit}>

            <div className="cf-grid">

              <div className="cf-field">
              <label>Challan Number *</label>
              <div style={{ position: "relative" }}>
                <input
                  name="challanNumber"
                  value={form.challanNumber}
                  onChange={handleChange}
                  style={{
                    paddingRight: challanNumberChecking || challanNumberValid !== null ? "36px" : "0",
                    borderColor: challanNumberValid === false ? "#ef4444" : challanNumberValid === true ? "#22c55e" : "",
                  }}
                />
                {challanNumberChecking && (
                  <span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", color: "#6b7280" }}>
                    ⏳
                  </span>
                )}
                {challanNumberValid === true && !challanNumberChecking && (
                  <span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", color: "#22c55e" }}>
                    ✓
                  </span>
                )}
                {challanNumberValid === false && (
                  <span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", color: "#ef4444" }}>
                    ✗
                  </span>
                )}
              </div>
              {challanNumberError && (
                <span style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  {challanNumberError}
                </span>
              )}
            </div>

            {/* <div className="cf-field">
              <label>Batch No *</label>
              <input
                name="batchNo"
                value={form.batchNo}
                onChange={handleChange}
              />
            </div> */}

            <div className="cf-field full">
              <label>Customer Name *</label>
              <input
                name="customer"
                value={form.customer}
                onChange={handleChange}
              />
            </div>
             <div className="cf-field full">
              <label>Customer ID*</label>
              <input
                name="customerId"
                value={form.customerId}
                onChange={handleChange}
              />
            </div>

            <div className="cf-field">
              <label>Challan Type *</label>
              <select
                name="challanType"
                value={form.challanType}
                onChange={handleChange}
              >
                <option value="">Select Type</option>
                <option value="DELIVERED">Delivered</option>
                <option value="RECEIVED">Received</option>
              </select>
            </div>

            <div className="cf-field">
              <label>Date *</label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
              />
            </div>

            <div className="cf-field full">
              <label>Site Location *</label>
              <input
                name="siteLocation"
                value={form.siteLocation}
                onChange={handleChange}
              />
            </div>

            <div className="cf-field">
              <label>Vehicle Number *</label>
              <input
                name="vehicleNo"
                value={form.vehicleNo}
                onChange={handleChange}
              />
            </div>

            <div className="cf-field">
              <label>Transporter *</label>
              <input
                name="transporter"
                value={form.transporter}
                onChange={handleChange}
              />
            </div>

            <div className="cf-field full">
              <label>Driver Details</label>
              <input
                name="driverDetails"
                value={form.driverDetails}
                onChange={handleChange}
              />
            </div>

            <div className="cf-field full">
              <label>Delivery Details</label>
              <input
                name="deliveryDetails"
                value={form.deliveryDetails}
                onChange={handleChange}
              />
            </div>

            <div className="cf-field">
              <label>PO No</label>
              <input
                name="poNo"
                value={form.poNo}
                onChange={handleChange}
              />
            </div>

            <div className="cf-field">
              <label>Deposit Amount</label>

              <div className="cf-deposit">

                <select
                  name="depositType"
                  value={form.depositType}
                  onChange={handleChange}
                >
                  <option>Deposit</option>
                  <option>Returned</option>
                </select>

                <input
                  name="depositAmount"
                  value={displayDepositAmount}
                  onChange={(e) => {
                    const nextValue = e.target.value.replace(/^-/, "");
                    setForm(prev => ({ ...prev, depositAmount: nextValue }));
                  }}
                  placeholder="0.00"
                />

              </div>

            </div>

            <div className="cf-field full">
              <label>Deposit Narration</label>
              <input
                name="depositNarration"
                value={form.depositNarration}
                onChange={handleChange}
              />
            </div>

          </div>

          <div className="cf-product-btn">

            <button
              type="button"
              className="btn-insert-product"
              onClick={() => setShowInsertProduct(!showInsertProduct)}
            >
              {showInsertProduct ? "Hide Product" : "Insert Product"}
            </button>

          </div>

          {showInsertProduct && (
            <div className="insert-product-inline">
              <InsertProduct
                onProductsAdded={handleProductsAdded}
                onClose={() => setShowInsertProduct(false)}
                isInModal={false}
              />
            </div>
          )}

          <div className="cf-buttons">

            <button
              type="button"
              className="btn-outline"
              onClick={() => navigate("/dashboard/challans/view")}
            >
              Cancel
            </button>

            <button
              type="button"
              className="btn-outline"
              onClick={handleReset}
            >
              Reset
            </button>

            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Challan"}
            </button>

          </div>

        </form>

      </div>

    </div>
    </>
  );
}

export default ChallanForm;
